import { Component, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { ThemeBorder, ThemeElevated } from '@frontend/sports/common/ui/shared';
import { SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { ComponentLoaderComponent } from '@frontend/sports/deferred/feature/component-loader';
import { NOT_APPLICABLE } from '@frontend/sports/tracking/feature';
import { BannerType, BannerViewModel } from '@frontend/sports/types/components/banners/models';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { DsCard } from '@frontend/ui/card';
import { DeviceService } from '@frontend/vanilla/core';
import { linkedTransferState } from '@frontend/vanilla/ssr';
import { isEmpty } from 'lodash-es';

import { ArcRestrictionService } from '../arc/arc-restriction.service';
import { BannerEsportComponent } from './banner-esport.component';
import { BannerGamesComponent } from './banner-games.component';
import { BannerJackpotComponent } from './banner-jackpot.component';
import { BannerTrackingService } from './banner-tracking.service';
import { ofType } from './banner.model';
import { CasinoBannerLinkComponent } from './casino-banner-link.component';
import { SportBannerLinkComponent } from './sport-banner-link.component';
import { VymlComponent } from './vyml.component';
import { VymlService } from './vyml.service';

@Component({
    selector: 'ms-banner-widget',
    templateUrl: './banner-widget.component.html',
    styleUrl: './banner-widget.component.scss',
    imports: [
        BannerEsportComponent,
        BannerGamesComponent,
        BannerJackpotComponent,
        CasinoBannerLinkComponent,
        ComponentLoaderComponent,
        SportBannerLinkComponent,
        SportsThemedCardDirective,
        VymlComponent,
        DsCard,
    ],
    host: {
        'class': 'banner-widget',
        '[class.esport]': 'esportClass()',
        '[style.--component-loader-height]': 'bannerComponentSize()',
        '[style.--component-loader-padding]': '0',
    },
    providers: [BannerTrackingService, VymlService],
})
export class BannerWidgetComponent extends WidgetComponent<BannerViewModel> {
    private device = inject(DeviceService);
    private arcRestrictionService = inject(ArcRestrictionService);
    private epcotConfig = inject(EpcotConfig);

    readonly banner = linkedTransferState<BannerViewModel | undefined>(() => `banner-widget-${this.config?.location}-${this.config?.id}`, undefined);

    readonly esportClass = computed(() => this.banner()?.type === BannerType.Esport);

    readonly bannerComponentSize = computed(() => {
        if (!this.showLoader()) {
            return null;
        }

        switch (this.banner()?.type) {
            case BannerType.CasinoLink:
                return '70px';
            case BannerType.Games:
            case BannerType.Vyml:
                return '137px';
            case BannerType.Jackpot:
                return '100px';
            case BannerType.SportLink:
                return '76px';
            case BannerType.Esport:
                return '82px';
            default:
                return null;
        }
    });

    position = NOT_APPLICABLE;

    readonly hideGamingCrossSellByArc!: Signal<boolean | undefined>;

    readonly showLoader = signal(true);

    readonly isEpcotEnabled = signal(false);
    readonly isMobile = signal(false);

    themeElevated: ThemeElevated = {
        'sportingbet-br': { isElevated: false },
        'betboo-br': { isElevated: false },
    };
    themeBorder: ThemeBorder = {
        'sportingbet-br': { hasBorder: true },
        'betboo-br': { hasBorder: true },
    };

    constructor() {
        super();
        const hideGamingCrossSellByArc$ = this.arcRestrictionService.gamingCrossSellEnabled$;
        this.hideGamingCrossSellByArc = toSignal(hideGamingCrossSellByArc$);

        this.isMobile.update(() => this.device.isMobile);
        this.isEpcotEnabled.update(() => this.epcotConfig.isEnabled);

        if (this.banner()) {
            // if data is available from transferCache
            this.bannerDataLoaded(true);
        }
    }

    protected override onData(data?: BannerViewModel): void {
        if (this.device.isRobot) {
            this.hasData = false;
        } else {
            this.hasData = !isEmpty(data);
            this.banner.set(data);
        }

        this.setWidgetLoaded();
    }

    bannerDataLoaded(hasBannerData: boolean): void {
        this.hasData = hasBannerData;
        this.showLoader.set(false);
        this.setWidgetLoaded();
    }

    protected readonly ofType = ofType;
}
